import { date_dmy_func, isFoodERPDotIn } from "../../components/Common/CommonFunction";
import { numberWithCommas } from "../Report_common_function";

/**
 * ChallanTableData.js — RA Masale "Delivery challan" format
 * ------------------------------------------------------------------
 * Pure data functions only (no doc.* drawing calls) — same split as
 * your Tax Invoice TableData.js: this file returns arrays/rows/
 * strings, ChallanReportStyle.js turns them into doc.text /
 * autoTable calls.
 *
 * UPDATED against your real IB_Invoice_Singel_Get_for_Report_Api
 * response. Key correction from the first draft: in that response
 * "Party" is YOUR company (seller — PartyName/PartyGSTIN/PartyAddress
 * carry values like a full company profile: GSTIN + FSSAI + State),
 * while "Customer" is the receiving party — the one printed in the
 * "Party Details :" box on the sample challan. So this file now reads
 * Customer* for the receiver box; Party* is used in
 * ChallanReportStyle.js for the seller letterhead instead.
 *
 * Still not present anywhere in the sample response: TripNo. Left
 * wired to `data.TripNo` below so it picks up automatically the
 * moment that field exists on the API — until then it just prints
 * blank. Tell me the real field name (if any) and I'll wire it.
 * ------------------------------------------------------------------
 */

// Columns exactly as printed on the sample: S.N. | Description of
// Goods | Qty. | Fr. | Unit — no HSN/MRP/Rate/Amount, since a
// delivery challan (unlike the tax invoice) carries no pricing, even
// though the API response itself does include pricing fields.
export const columns = () =>
    isFoodERPDotIn()
        ? ["S.N.", "Item Name", "Bulk Qty", "Bulk Qty Unit", "No of Qty", "Unit"]
        : ["S.N.", "Description of Goods", "Qty", "Unit"];

// Column widths in points. MUST sum to <= (page width - left/right
// margins) = 595 - 30 - 30 = 535. Kept at 530 for headroom.


export const columnWidths = () =>
    isFoodERPDotIn()
        ? { 0: 30, 1: 220, 2: 60, 3: 80, 4: 80, 5: 70 }
        : { 0: 30, 1: 320, 2: 120, 3: 70 };

/**
 * Table rows, sourced from `data.InvoiceItems` (that's the real
 * array name in your response, even on the challan endpoint — not
 * "ChallanItems" as first guessed). Also accumulates total quantity
 * onto `data.footerTotals` for the Grand Total line.
 */
export const Rows = (data) => {
    const items = data.InvoiceItems || [];

    let totalQty = 0;
    let totalQtyInNo = 0;

    const returnArr = items.map((item, idx) => {
        totalQty += Number(item.Quantity) || 0;
        totalQtyInNo += Number(item.BaseUnitQuantity) || 0;

        if (isFoodERPDotIn()) {
            // "Box (1 Kg)" -> "Kg"
            const m = (item.UnitName || "").match(/\(([^)]+)\)/);
            const baseUnit = m ? m[1].replace(/^[\d.,\s]+/, "").trim() : "";

            return [
                idx + 1,
                item.ItemName || "",
                `${parseFloat(item.Quantity) || 0}`,      // Bulk Qty
                item.PrimaryUnitName || "",               // Bulk Qty Unit (Box)
                `${parseFloat(item.BaseUnitQuantity) || 0}`,  // No of Qty
                baseUnit || item.UnitName || "",          // Unit (Kg)
            ];
        }

        return [
            idx + 1,
            item.ItemName || "",
            `${parseFloat(item.Quantity) || 0}`,
            item.PrimaryUnitName || item.UnitName || "",
        ];
    });

    data["TotalItemlength"] = items.length;
    data["footerTotals"] = { totalQty, totalQtyInNo };

    return returnArr;
};

/**
 * "Party Details :" box — the RECEIVING party, i.e. `data.Customer*`
 * in your response (matches how the Tax Invoice's own PartyDetails()
 * uses CustomerName/CustomerAddress/CustomerGSTIN for its buyer box).
 * Handles CustomerAddress as either a plain string (what your sample
 * response actually sends) or an array of {IsDefault, Address}
 * (what the invoice report defends against), so it works either way.
 */
export const PartyDetails = (data) => {
    let customerAddress = "";
    if (Array.isArray(data.CustomerAddress)) {
        const filteredArray = data.CustomerAddress.filter((obj) => obj.IsDefault === true);
        customerAddress = filteredArray[0] === undefined ? "" : filteredArray[0].Address;
    } else {
        customerAddress = data.CustomerAddress;
    }

    const gstinLine = data.CustomerGSTIN ? `GSTIN / UIN : ${data.CustomerGSTIN}` : "";

    return {
        name: data.CustomerName || "",
        refLine: "",   // no matching field on the response (sample image's "01, 01" line) — add one here if/when your API sends it
        altName: "",   // no matching field on the response (sample image's "M/S Biyani Chemical..." line) — same as above
        address: customerAddress || "",
        gstinLine,
    };
};

/**
 * Right-hand info block: Challan No / Dated / Driver Name /
 * Vehicle No / Trip No.
 *
 * "Challan No." reads `data.FullInvoiceNumber` — your sample shows
 * "CH944", i.e. this field is already the challan-numbered one even
 * though the key itself is called "Invoice".
 */
export const ChallanInfoRows = (data) => [
    { label: "Challan No.", value: data.FullInvoiceNumber || "" },
    { label: "Dated", value: date_dmy_func ? date_dmy_func(data.InvoiceDate) : data.InvoiceDate || "" },
    { label: "Driver Name", value: data.DriverName || "" },
    { label: "Vehicle No", value: data.VehicleNo || "" },
    { label: "Trip No", value: data.TripNo || "" },
];

export { numberWithCommas };